import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Popover,
  Stack,
  Divider,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { DateRange, Range, RangeKeyDict } from "react-date-range";
import {
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  format,
  isSameDay,
  startOfYear,
  subYears,
  endOfDay
} from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  BRAND_COLOR,
  BRAND_DISABLED,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { getDateFnsLocale } from "../../Utils/dateFnsLocale";
import { MetricTimeRange } from "./types/dashboardTypes";

function defaultSelectionRange(): Range {
  const end = new Date();
  return {
    startDate: subDays(end, 6),
    endDate: end,
    key: "selection"
  };
}

function toSelectionRange(v: MetricTimeRange): Range {
  return {
    startDate: v.startDate,
    endDate: v.endDate,
    key: "selection"
  };
}

interface DateRangeSelectorProps {
  onChange: (range: { startDate: Date; endDate: Date }) => void;
  /** When set (e.g. from Redux), keeps the button label in sync after remounts (refetch / full-page loader). */
  value?: MetricTimeRange;
}

type PresetId =
  | "today"
  | "this-month"
  | "ytd"
  | "yesterday"
  | "last-7"
  | "last-month"
  | "1y"
  | "5y";

interface PresetDefinition {
  id: PresetId;
  labelKey: string;
  defaultLabel: string;
  range: () => { startDate: Date; endDate: Date };
}

const PRESET_DEFINITIONS: PresetDefinition[] = [
  {
    id: "today",
    labelKey: "dashboard-date-preset-today",
    defaultLabel: "Today",
    range: () => ({ startDate: new Date(), endDate: new Date() })
  },
  {
    id: "this-month",
    labelKey: "dashboard-date-preset-this-month",
    defaultLabel: "This Month",
    range: () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date())
    })
  },
  {
    id: "ytd",
    labelKey: "dashboard-date-preset-ytd",
    defaultLabel: "YTD",
    range: () => ({
      startDate: startOfYear(new Date()),
      endDate: new Date()
    })
  },
  {
    id: "yesterday",
    labelKey: "dashboard-date-preset-yesterday",
    defaultLabel: "Yesterday",
    range: () => ({
      startDate: subDays(new Date(), 1),
      endDate: subDays(new Date(), 1)
    })
  },
  {
    id: "last-7",
    labelKey: "dashboard-date-preset-last-7-days",
    defaultLabel: "Last 7 Days",
    range: () => ({
      startDate: subDays(new Date(), 6),
      endDate: new Date()
    })
  },
  {
    id: "last-month",
    labelKey: "dashboard-date-preset-last-month",
    defaultLabel: "Last Month",
    range: () => {
      const start = startOfMonth(subMonths(new Date(), 1));
      const end = endOfMonth(subMonths(new Date(), 1));
      return { startDate: start, endDate: end };
    }
  },
  {
    id: "1y",
    labelKey: "dashboard-date-preset-1-year",
    defaultLabel: "1 Year",
    range: () => ({
      startDate: subYears(new Date(), 1),
      endDate: new Date()
    })
  },
  {
    id: "5y",
    labelKey: "dashboard-date-preset-5-years",
    defaultLabel: "5 Years",
    range: () => ({
      startDate: subYears(new Date(), 5),
      endDate: new Date()
    })
  }
];

const DateRangeSelector: FC<DateRangeSelectorProps> = ({ onChange, value }) => {
  const { t, i18n } = useKivunovaTranslation();
  const dateFnsLocale = useMemo(
    () => getDateFnsLocale(i18n.language),
    [i18n.language]
  );

  const calendarAriaLabels = useMemo(
    () => ({
      prevButton: t("dashboard-date-aria-prev-month", "Previous month"),
      nextButton: t("dashboard-date-aria-next-month", "Next month"),
      monthPicker: t("dashboard-date-aria-month-picker", "Choose month"),
      yearPicker: t("dashboard-date-aria-year-picker", "Choose year"),
      dateInput: {
        selection: {
          startDate: t("dashboard-date-aria-start-date", "Start date"),
          endDate: t("dashboard-date-aria-end-date", "End date")
        }
      }
    }),
    [t, i18n.language]
  );

  const presets = useMemo(
    () =>
      PRESET_DEFINITIONS.map((p) => ({
        ...p,
        label: t(p.labelKey, p.defaultLabel)
      })),
    [t, i18n.language]
  );

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [range, setRange] = useState<Range>(() =>
    value ? toSelectionRange(value) : defaultSelectionRange()
  );
  const [tempRange, setTempRange] = useState<Range>(() =>
    value ? toSelectionRange(value) : defaultSelectionRange()
  );

  const valueSyncKey =
    value && `${value.startDate.getTime()}-${value.endDate.getTime()}`;

  useEffect(() => {
    if (!value?.startDate || !value?.endDate) return;
    const next = toSelectionRange(value);
    setRange(next);
    setTempRange(next);
  }, [valueSyncKey]);

  // === Helper to check if current range matches preset ===
  const isPresetActive = (preset: (typeof presets)[number]) => {
    const presetRange = preset.range();
    return (
      isSameDay(tempRange.startDate!, presetRange.startDate!) &&
      isSameDay(tempRange.endDate!, presetRange.endDate!)
    );
  };

  // === Handlers ===
  const handleSelect = (ranges: RangeKeyDict) => {
    const selection = ranges.selection;
    if (selection.startDate && selection.endDate) {
      setTempRange(selection);
    }
  };

  const handlePresetClick = (preset: (typeof presets)[number]) => {
    const newRange = preset.range();
    setTempRange({ ...newRange, key: "selection" });
  };

  const handleApply = () => {
    setRange(tempRange);
    onChange({ startDate: tempRange.startDate!, endDate: tempRange.endDate! });
    setAnchorEl(null);
  };

  const handleCancel = () => {
    setTempRange(range); // revert
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const formatted = useMemo(() => {
    if (!range.startDate || !range.endDate) {
      return t("dashboard-date-select-range", "Select Date Range");
    }
    const opts = { locale: dateFnsLocale };
    return `${format(range.startDate, "MMM d, yyyy", opts)} – ${format(
      range.endDate,
      "MMM d, yyyy",
      opts
    )}`;
  }, [range.startDate, range.endDate, dateFnsLocale, t, i18n.language]);

  return (
    <>
      {/* Trigger button */}
      <Button
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          textTransform: "none",
          minWidth: 250,
          justifyContent: "space-between",
          color: "text.primary",
          borderColor: "#d2c8f7",
          "&:hover": { borderColor: "#5b36b0" }
        }}
      >
        {formatted}
      </Button>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
            width: isSmall ? "95vw" : "auto",
            maxWidth: "90vw",
            overflowX: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
          }
        }}
      >
        <Stack
          direction={isSmall ? "column" : "row"}
          spacing={isSmall ? 2 : 0}
          alignItems="stretch"
        >
          {/* Shortcuts */}
          {isSmall ? (
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 1,
                pb: 1,
                "&::-webkit-scrollbar": { display: "none" }
              }}
            >
              {presets.map((preset) => {
                const active = isPresetActive(preset);
                return (
                  <Button
                    key={preset.id}
                    onClick={() => handlePresetClick(preset)}
                    variant={active ? "contained" : "outlined"}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      borderColor: BRAND_COLOR,
                      color: active ? "white" : BRAND_COLOR,
                      backgroundColor: active ? BRAND_COLOR : "transparent",
                      "&:hover": {
                        backgroundColor: BRAND_HOVER,
                        color: "white"
                      },
                      textTransform: "none"
                    }}
                  >
                    {preset.label}
                  </Button>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ pr: 2, borderRight: "1px solid #eee", minWidth: 160 }}>
              {presets.map((preset) => {
                const active = isPresetActive(preset);
                return (
                  <Button
                    key={preset.id}
                    onClick={() => handlePresetClick(preset)}
                    sx={{
                      display: "block",
                      textTransform: "none",
                      justifyContent: "flex-start",
                      width: "100%",
                      mb: 0.6,
                      border: `1px solid ${BRAND_COLOR}`,
                      color: active ? "white" : BRAND_COLOR,
                      backgroundColor: active ? BRAND_COLOR : "white",
                      "&:hover": {
                        backgroundColor: BRAND_HOVER,
                        color: "white"
                      }
                    }}
                  >
                    {preset.label}
                  </Button>
                );
              })}
            </Box>
          )}

          {/* Calendar */}
          <Box>
            <DateRange
              onChange={handleSelect}
              moveRangeOnFirstSelection={false}
              ranges={[tempRange]}
              maxDate={endOfDay(new Date())}
              direction={isSmall ? "vertical" : "horizontal"}
              showMonthAndYearPickers
              locale={dateFnsLocale}
              ariaLabels={calendarAriaLabels}
              startDatePlaceholder={t(
                "dashboard-date-placeholder-start",
                "Start"
              )}
              endDatePlaceholder={t("dashboard-date-placeholder-end", "End")}
              rangeColors={["#b291f8"]} // primary range highlight
            />

            <Divider sx={{ mt: 2, mb: 1 }} />

            {/* Footer buttons */}
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                onClick={handleCancel}
                size="small"
                sx={{
                  backgroundColor: "white",
                  color: BRAND_COLOR,
                  border: `1px solid ${BRAND_COLOR}`,
                  "&:hover": { backgroundColor: BRAND_HOVER, color: "white" },
                  "&:disabled": { backgroundColor: BRAND_DISABLED },
                  textTransform: "none",
                  fontSize: "1rem"
                }}
              >
                {t("dashboard-date-cancel", "Cancel")}
              </Button>
              <Button
                onClick={handleApply}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: BRAND_COLOR,
                  "&:hover": { backgroundColor: BRAND_HOVER },
                  textTransform: "none"
                }}
                disabled={!tempRange.startDate || !tempRange.endDate}
              >
                {t("dashboard-date-apply", "Apply")}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Popover>
    </>
  );
};

export default DateRangeSelector;
